import { jsPDF } from 'jspdf';
import { clean, mapSnapshot, overviewView, safeName } from './exporters';
import { APP_INFO } from './license';
import { companyLine } from './company';
import { money, locale } from './units';
import { woodPiles, woodVol, num } from './woodList';

const nf = (v, d = 2) => (+v || 0).toLocaleString(locale(), { minimumFractionDigits: d, maximumFractionDigits: d });
export const woodSorts = (tr) => [...new Set(woodPiles(tr).map((w) => w.data?.sort || '–'))];
const nav = (w) => `https://www.google.com/maps/dir/?api=1&destination=${w.lat.toFixed(6)},${w.lon.toFixed(6)}`;

// timber list for the buyer: site map with numbered piles, table with prices, photos, coordinates + navi link, signatures
export async function woodPdf(tr, t, project, { prices = {}, seller = '', buyer = '' } = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }), M = 14, W = 182;
  const list = woodPiles(tr), media = (w) => ((w.fid ? project.fittings.find((f) => f.id === w.fid) : (project.wpMedia || []).find((m) => m.id === w.id))?.media || []).filter((m) => m.kind === 'photo' && m.data);
  let y = 0;
  const head = () => {
    doc.setFillColor(68, 41, 14); doc.rect(0, 0, 210, 24, 'F');
    doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text(clean(`${t.woodTitle}: ${tr.name}`), M, 11);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(clean([companyLine(), project.title, new Date().toLocaleDateString(locale())].filter(Boolean).join(' · ')), M, 18);
    doc.setTextColor(20); y = 32;
  };
  const ensure = (h) => { if (y + h > 282) { doc.addPage(); head(); } };
  head();
  const pts = list.map((w) => [w.lat, w.lon]);
  try {
    const img = await mapSnapshot(overviewView(pts.length > 1 ? pts : [...pts, ...tr.pts.slice(0, 1).map((p) => [p.lat, p.lon])]), [{ pts: tr.pts.map((p) => [p.lat, p.lon]), color: '#92400e' }], { noCross: true, markers: list.map((w, i) => ({ pt: [w.lat, w.lon], n: i + 1, color: '#92400e' })) }, 900, 520);
    if (img) { doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(clean(t.woodMap), M, y); doc.addImage(img, 'JPEG', M, y + 2, W, W * 520 / 900); y += W * 520 / 900 + 8; }
  } catch { /* offline: no map */ }
  const X = [M, M + 8, M + 50, M + 80, M + 98, M + 116, M + 132, M + 150, M + 166, M + 182];
  const cols = [t.woodNo, `${t.wpF_species} / ${t.wpF_sort}`, t.wpF_grade, t.wpF_len, t.wpF_dia, t.wpF_count, t.wpF_volume, `${t.woodPrice}`, t.woodValue];
  const row = (c, bold) => {
    const l1 = doc.splitTextToSize(clean(String(c[1])), 40);
    ensure(l1.length * 4 + 3); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(bold ? 7.5 : 8.5);
    c.forEach((v, i) => (i === 1 ? doc.text(l1, X[1], y) : doc.text(clean(String(v)), i > 2 ? X[i + 1] - 1 : X[i], y, { align: i > 2 ? 'right' : 'left', maxWidth: i > 2 ? 18 : 28 })));
    y += l1.length * 4 + 1.5; doc.setDrawColor(226, 232, 240); doc.line(M, y - 2.5, M + W, y - 2.5);
  };
  ensure(12); row(cols, true);
  let sum = 0, val = 0;
  const bySort = {};
  list.forEach((w, i) => {
    const d = w.data || {}, v = woodVol(d), s = d.sort || '–', p = num(prices[s]), a = v * p;
    sum += v; val += a; bySort[s] = bySort[s] || { v: 0, a: 0 }; bySort[s].v += v; bySort[s].a += a;
    row([i + 1, [d.species, d.sort].filter(Boolean).join(' / ') || '–', d.grade || '–', num(d.len) ? nf(num(d.len), 1) : '–', num(d.dia) ? nf(num(d.dia), 0) : '–', num(d.count) || (num(d.len) ? 1 : '–'), nf(v), p ? money(p) : '–', p ? money(a) : '–']);
  });
  ensure(10); y += 2; doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(clean(`${t.woodSum}: ${nf(sum)} fm`), M, y); if (val) doc.text(clean(`${t.woodTotalValue}: ${money(val)}`), M + W, y, { align: 'right' }); y += 7;
  doc.setFontSize(9); doc.text(clean(t.woodBySort), M, y); y += 5; doc.setFont('helvetica', 'normal');
  Object.entries(bySort).forEach(([k, o]) => { ensure(5); doc.text(clean(`${k}: ${nf(o.v)} fm${num(prices[k]) ? ` × ${money(num(prices[k]))} = ${money(o.a)}` : ''}`), M + 4, y); y += 4.6; });
  y += 4; ensure(12); doc.setFont('helvetica', 'bold'); doc.text(clean(t.woodPiles), M, y); y += 5;
  list.forEach((w, i) => {
    const ph = media(w).slice(0, 3), h = ph.length ? 44 : 14;
    ensure(h + 4); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(clean(`${i + 1}. R${tr.no || 1}-P${w.no || i + 1} · ${[w.data?.species, w.data?.sort].filter(Boolean).join(' / ')} · ${nf(woodVol(w.data))} fm`), M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(clean(`${w.lat.toFixed(6)}, ${w.lon.toFixed(6)}${w.note ? ` · ${w.note}` : ''}`), M, y + 4.5);
    doc.setTextColor(29, 78, 216); doc.textWithLink(clean(t.woodNav), M + W, y + 4.5, { url: nav(w), align: 'right' }); doc.setTextColor(20);
    ph.forEach((m, k) => { try { doc.addImage(m.data, 'JPEG', M + k * 61, y + 7, 58, 34); } catch { /* broken image */ } });
    y += h + 3;
  });
  ensure(40); y += 8; doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  [[t.woodSeller, seller], [t.woodBuyer, buyer]].forEach(([k, n], i) => {
    const x = M + i * 96;
    doc.line(x, y + 16, x + 80, y + 16); doc.text(clean(`${k}${n ? `: ${n}` : ''}`), x, y + 21); doc.text(clean(`${t.woodDate} / ${t.woodSign}`), x, y + 25.5);
  });
  doc.setFontSize(7); doc.setTextColor(120); doc.text(clean(APP_INFO.name), M, 290);
  doc.save(`${safeName(`${tr.name || 'Holzliste'}`)}_Holzliste.pdf`);
  return { sum, val };
}
